import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import AdminNavbar from './AdminNavbar';
import axios from 'axios';
import { Plus, Loader2, Folder, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { HubFilters } from './hub/HubFilters';
import { AppCard, AppListItem } from './hub/AppCard';
import { AppFormModal } from './hub/AppFormModal';
import { EMPTY_APP_FORM } from './hub/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppHub = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [showAddApp, setShowAddApp] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_APP_FORM);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    let active = true;
    (async () => {
      try {
        const response = await axios.get(`${API}/hub/check-admin`, { withCredentials: true });
        if (!active) return;
        const isAdmin = response.data.is_super_admin;
        setIsSuperAdmin(isAdmin);
        if (isAdmin) {
          const appsRes = await axios.get(`${API}/hub/apps`, { withCredentials: true });
          if (active) setApps(appsRes.data || []);
        }
      } catch (err) {
        if (active) setIsSuperAdmin(false);
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [authLoading, isAuthenticated]);

  const fetchApps = async () => {
    try {
      const response = await axios.get(`${API}/hub/apps`, { withCredentials: true });
      setApps(response.data || []);
    } catch (error) {
      toast.error('Failed to load apps');
    }
  };

  const closeModal = () => {
    setShowAddApp(false);
    setEditingApp(null);
    setFormData(EMPTY_APP_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.admin_url) {
      toast.error('Name and Admin URL are required');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingApp) {
        await axios.put(`${API}/hub/apps/${editingApp.app_id}`, formData, { withCredentials: true });
        toast.success('App updated successfully');
      } else {
        await axios.post(`${API}/hub/apps`, formData, { withCredentials: true });
        toast.success('App added successfully');
      }
      closeModal();
      fetchApps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this app?')) return;
    try {
      await axios.delete(`${API}/hub/apps/${appId}`, { withCredentials: true });
      toast.success('App deleted');
      fetchApps();
    } catch (error) {
      toast.error('Failed to delete app');
    }
  };

  const handleOpenApp = async (app) => {
    try {
      await axios.post(`${API}/hub/apps/${app.app_id}/access`, {}, { withCredentials: true });
    } catch (error) {
      // Best-effort access logging; ignore failures
    }
    window.open(app.admin_url, '_blank');
  };

  const openAddModal = () => {
    setFormData(EMPTY_APP_FORM);
    setEditingApp(null);
    setShowAddApp(true);
  };

  const openEditModal = (app) => {
    setFormData({
      name: app.name,
      description: app.description || '',
      admin_url: app.admin_url,
      icon_url: app.icon_url || '',
      category: app.category,
      status: app.status,
      deployment_url: app.deployment_url || '',
      deployment_status: app.deployment_status || 'deployed',
      team_members: app.team_members || [],
      notes: app.notes || ''
    });
    setEditingApp(app);
    setShowAddApp(true);
  };

  const filteredApps = apps.filter((app) => {
    const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          app.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || app.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-[#1a1c1b]">
        <AdminNavbar />
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 80px)' }}>
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-[#7dd3fc] animate-spin mx-auto mb-4" />
            <p className="text-[#888680]">Loading App Hub...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-[#1a1c1b]">
        <AdminNavbar showHubLink={false} />
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 80px)' }}>
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-[#7dd3fc] font-bold text-2xl mb-2">Access Denied</h2>
            <p className="text-[#888680] mb-6">You need super admin privileges to access the App Hub.</p>
            <Button
              onClick={() => navigate('/admin/dashboard')}
              className="bg-[#7dd3fc] text-[#1a1c1b] rounded-full px-6 py-3"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <AdminNavbar />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Title & Add Button */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-[#7dd3fc] font-bold text-3xl mb-2">App Hub</h1>
            <p className="text-[#888680]">Centralized management for all your apps</p>
          </div>
          <Button
            onClick={openAddModal}
            data-testid="add-app-btn"
            className="bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full px-6 py-3 font-semibold flex items-center gap-2"
          >
            <Plus size={20} />
            Add App
          </Button>
        </div>

        <HubFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />

        {filteredApps.length === 0 ? (
          <div className="text-center py-20">
            <Folder className="w-16 h-16 text-[#1e3a5f] mx-auto mb-4" />
            <h3 className="text-[#7dd3fc] font-bold text-xl mb-2">No Apps Found</h3>
            <p className="text-[#888680]">
              {searchQuery ? 'Try a different search term' : 'Add your first app to get started'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredApps.map((app) => (
              <AppCard
                key={app.app_id}
                app={app}
                onOpen={handleOpenApp}
                onEdit={openEditModal}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredApps.map((app) => (
              <AppListItem
                key={app.app_id}
                app={app}
                onOpen={handleOpenApp}
                onEdit={openEditModal}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </main>

      {showAddApp && (
        <AppFormModal
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onClose={closeModal}
          isSubmitting={isSubmitting}
          editingApp={editingApp}
        />
      )}
    </div>
  );
};

export default AppHub;
